<template>
  <v-dialog v-model="internalShow" max-width="1100" scrollable @click:outside="closeModal">
    <v-card v-if="details" class="rounded-lg">
      <!-- Encabezado con Folio y Estatus Global -->
      <v-card-title class="primary white--text d-flex justify-space-between align-center py-3">
        <span class="headline font-weight-bold">
          <v-icon dark left>mdi-package-variant-closed</v-icon>
          Orden de Venta #{{ details.folio }}
        </span>
        <v-chip dark small :color="getOverallStatusColor(details.overall_delivery_status_id)" class="font-weight-bold">
          {{ details.overall_delivery_status_description }}
        </v-chip>
      </v-card-title>

      <v-card-text class="pt-4">
        <!-- Información General (Cabecera) -->
        <v-row class="mb-2">
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">FECHA COMPRA</div>
            <div class="body-1 text--primary font-weight-medium">{{ formatDate(details.purchase_date) }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">CLIENTE</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.client_name }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">VENDEDOR</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.employee_name }}</div>
          </v-col>
          <v-col cols="12" sm="3">
            <div class="caption grey--text text--darken-1 font-weight-bold">SUCURSAL ORIGEN</div>
            <div class="body-1 text--primary font-weight-medium">{{ details.origin_store_name }}</div>
          </v-col>
        </v-row>

        <v-divider class="my-3"></v-divider>

        <v-window v-model="breakdownView">
          <v-window-item :value="0">
            <!-- Tarjetas de Resumen Logístico y Financiero -->
            <v-row class="mb-3">
              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 text-center fill-height background-light">
                  <div class="caption grey--text font-weight-bold">RESUMEN ARTÍCULOS</div>
                  <div class="d-flex justify-space-around align-center mt-2">
                    <div>
                      <div class="caption grey--text">Comprados</div>
                      <div class="title font-weight-bold primary--text">{{ details.total_items_ordered }}</div>
                    </div>
                    <div>
                      <div class="caption grey--text">Entregados</div>
                      <div class="title font-weight-bold success--text">{{ details.total_items_delivered }}</div>
                    </div>
                    <div>
                      <div class="caption grey--text">Pendientes</div>
                      <div class="title font-weight-bold warning--text">{{ details.total_items_pending }}</div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 text-center fill-height background-light">
                  <div class="caption grey--text font-weight-bold">TOTAL VENTA</div>
                  <div class="display-1 font-weight-bold primary--text mt-1">
                    {{ formatMoney(details.total_amount) }}
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="4">
                <v-card outlined class="pa-3 fill-height background-light">
                  <div class="caption grey--text text-center font-weight-bold mb-1">MÉTODOS DE PAGO</div>
                  <div v-for="(pay, idx) in details.payments" :key="idx" class="d-flex justify-space-between caption">
                    <span>{{ pay.payment_method }}:</span>
                    <span class="font-weight-bold">{{ formatMoney(pay.amount) }}</span>
                  </div>
                  <div v-if="!details.payments || details.payments.length === 0" class="caption text-center grey--text">
                    Sin desglose
                  </div>
                </v-card>
              </v-col>
            </v-row>

            <!-- Listado de Productos -->
            <div class="d-flex justify-space-between align-center mb-2">
              <div class="subtitle-1 font-weight-bold primary--text">Desglose de Productos</div>
              <div v-if="details.overall_delivery_status_id !== 3">
                <v-btn
                  v-if="breakdownView === 1"
                  small
                  outlined
                  color="error"
                  @click="cancelDistribution"
                >Cancelar</v-btn>
                <v-btn small outlined @click="distributionOnClick" class="ml-2">
                  {{ breakdownView === 0 ? 'Reasignar' : 'Guardar cambios' }}
                </v-btn>
              </div>
            </div>

            <v-simple-table dense class="elevation-1 border-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Producto</th>
                    <th class="text-center">Comprados</th>
                    <th class="text-center">Entregados</th>
                    <th class="text-center">Pendientes</th>
                    <th class="text-center">Sucursal de Entrega</th>
                    <th class="text-right">$ Unid.</th>
                    <th class="text-right">Total</th>
                    <th class="text-center">Estatus</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in details.items" :key="item.product_id">
                    <td class="font-weight-medium">{{ item.product_name }}</td>
                    <td class="text-center font-weight-bold">{{ item.quantity_ordered }}</td>
                    <td class="text-center success--text font-weight-bold">{{ item.quantity_delivered }}</td>
                    <td class="text-center warning--text font-weight-bold">{{ item.quantity_pending }}</td>
                    <td class="text-center font-weight-medium">
                      <v-menu v-if="hasMultiplePickupStores(item)" open-on-hover offset-y>
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn text small color="primary" v-bind="attrs" v-on="on">
                            <span>Varios</span>
                            <v-icon small>mdi-chevron-down</v-icon>
                          </v-btn>
                        </template>
                        <v-card class="delivery-store-card">
                          <v-card-title class="py-2 px-3">Sucursales de entrega</v-card-title>
                          <v-card-text class="py-2 px-3">
                            <v-list dense class="delivery-store-list pa-0">
                              <v-list-item
                                v-for="(storeInfo, storeInfoIndex) in getStoreDeliveryInfo(item)"
                                :key="storeInfoIndex"
                                class="px-0"
                              >
                                <v-list-item-content class="py-1">
                                  <v-list-item-title>
                                    <div>
                                      <div class="d-flex justify-space-between">
                                        <span>{{ storeInfo.pickup_store_name }}:</span>
                                        <!-- <span v-else>Entregados en mostrador:</span> -->
                                        <span>{{ storeInfo.quantity }}</span>
                                      </div>
                                    </div>
                                  </v-list-item-title>
                                </v-list-item-content>
                              </v-list-item>
                            </v-list>
                          </v-card-text>
                        </v-card>
                      </v-menu>
                      <span v-else>
                        {{ getRealPickupStores(item) }}
                      </span>
                    </td>
                    <td class="text-right">{{ formatMoney(item.unit_price) }}</td>
                    <td class="text-right font-weight-bold">{{ formatMoney(item.total_price) }}</td>
                    <td class="text-center">
                      <v-chip x-small dark :color="getItemStatusColor(item.delivery_status_id)">
                        {{ item.delivery_status_description }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-window-item>
          <v-window-item :value="1" eager>
            <v-text-field
              v-model="notes"
              dense
              outlined
              label="Notas *"
              class="mt-2">
            </v-text-field>
            <!-- Listado de Productos -->
            <div class="d-flex justify-space-between align-center mb-2">
              <div class="subtitle-1 font-weight-bold primary--text">Desglose de Productos</div>
            </div>
            <DeliveryDistributionTable
              ref="deliveryDistributionTable"
              :sale-folio="folio"
              :pickupStoreId="currentStoreId"
            />
            <div v-if="details.overall_delivery_status_id !== 3" class="d-flex justify-end mt-2">
              <v-btn
                v-if="breakdownView === 1"
                small
                outlined
                color="error"
                @click="cancelDistribution"
              >Cancelar</v-btn>
              <v-btn small outlined @click="distributionOnClick" class="ml-2">
                {{ breakdownView === 0 ? 'Reasignar' : 'Guardar cambios' }}
              </v-btn>
            </div>
          </v-window-item>
        </v-window>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions class="pa-3">
        <v-spacer></v-spacer>
        <v-btn color="grey darken-1" text @click="closeModal">Cerrar</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Estado de Carga / Error -->
    <v-card v-else class="pa-5 text-center">
      <v-progress-circular v-if="isLoading" indeterminate color="primary" size="50"></v-progress-circular>
      <div v-else class="text-subtitle-1 error--text">
        No se pudo cargar la información de la orden.
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';
import InventoryService from '@/services/InventoryService';
import ClientDeliveryService from '@/services/ClientDeliveryService';
import { getOverallStatusColor, getItemStatusColor } from '@/utils/Constants';
import DeliveryDistributionTable from './DeliveryDistributionTable.vue';

export default {
  components: {
    DeliveryDistributionTable,
  },
  name: 'DeliveryOrderDetailsModal',
  props: {
    value: { type: Boolean, default: false },
    folio: { type: [Number, String], default: null },
    storeId: { type: [Number, String], default: null },
  },
  data() {
    return {
      details: null,
      isLoading: false,
      breakdownView: 0,
      currentStoreId: null,
      notes: '',
    };
  },
  computed: {
    ...mapGetters(['authUserData']),
    internalShow: {
      get() { return this.value; },
      set(val) { this.$emit('input', val); },
    },
  },
  watch: {
    value(val) {
      if (val && this.folio) {
        this.fetchDetails();
      }
    },
  },
  methods: {
    getOverallStatusColor,
    getItemStatusColor,
    async fetchDetails() {
      this.isLoading = true;
      this.details = null;
      try {
        const response = await ClientDeliveryService.getDeliveryOrderDetail(this.folio, this.storeId);
        if (response.status === 'success' || response.success) {
          this.details = response.data;
        }
      } catch (error) {
        console.error('Error al cargar detalle:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al obtener detalles de la Venta',
          text: error,
          confirmButtonColor: 'primary',
        });
      } finally {
        this.isLoading = false;
      }
    },
    cancelDistribution() {
      this.notes = '';
      this.breakdownView = 0;
    },
    async distributionOnClick() {
      if (this.breakdownView === 0) {
        this.showDistributionModal();
      } else {
        const products = this.buildProductArray();
        const { products: assignedProducts, requiresConfirmation } = this.$refs.deliveryDistributionTable.getAssignedData(products);
        if (requiresConfirmation) {
          const result = await Swal.fire({
            icon: 'warning',
            title: 'Confirmar distribución',
            text: 'Stock local insuficiente para algunos de los productos. Al confirmar, la mercancía se solicitará a Almacén Central y el cliente deberá esperar la llegada del traspaso. ¿Desea continuar?',
            showCancelButton: true,
            confirmButtonColor: 'primary',
            cancelButtonColor: 'secondary',
            cancelButtonText: 'Cancelar',
          });
          if (!result.isConfirmed) {
            return;
          }
        }
        try {
          await InventoryService.saveSaleProductsDistribution(
            this.folio,
            this.storeId,
            assignedProducts,
            this.notes,
          );
          Swal.fire({
            icon: 'success',
            title: 'Distribución guardada',
            text: 'La distribución de productos se ha guardado correctamente.',
            confirmButtonColor: 'primary',
          });
          await this.fetchDetails();
          this.notes = '';
          this.hideDistributionModal();
        } catch (error) {
          console.error('Error saving distribution:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error al guardar la distribución',
            text: error,
            confirmButtonColor: 'primary',
          });
        }
      }
    },
    buildProductArray() {
      const products = [];
      this.details.items.forEach((item) => {
        item.store_delivery_info.forEach((info) => {
          for (let i = 0; i < info.quantity; i += 1) {
            products.push({
              productId: item.product_id,
            });
          }
        });
      });
      return products;
    },
    buildDistributionArray() {
      const items = [];
      const pendingItems = this.details.items.filter((item) => item.delivery_status_id !== 1 && item.delivery_status_id !== 5);
      pendingItems.forEach((item) => {
        const distributionItem = {
          id: item.product_id,
          barcode: '',
          name: item.product_name,
          backorder: item.store_delivery_info.reduce((sum, info) => sum + (info.quantity), 0),
        };
        item.store_delivery_info.forEach((info) => {
          distributionItem[`storeId${info.pickup_store_id}`] = info.quantity;
        });
        items.push(distributionItem);
      });
      return items;
    },
    async showDistributionModal() {
      const distributionArray = this.buildDistributionArray();
      this.$refs.deliveryDistributionTable.initializePickupDistribution(distributionArray);
      this.breakdownView = 1;
    },
    hideDistributionModal() {
      this.breakdownView = 0;
    },
    hasMultiplePickupStores(item) {
      const realStores = item.store_delivery_info.filter((storeInfo) => storeInfo.pickup_store_id !== null && storeInfo.quantity > 0);
      return realStores.length > 1;
    },
    getRealPickupStores(item) {
      const realStores = item.store_delivery_info.filter((storeInfo) => storeInfo.pickup_store_id !== null && storeInfo.quantity > 0);
      if (realStores.length === 0) {
        return '-';
      }
      return realStores[0].pickup_store_name;
    },
    getStoreDeliveryInfo(item) {
      if (item.store_delivery_info === null) {
        return [];
      }
      return item.store_delivery_info.filter((storeInfo) => storeInfo.quantity > 0 && storeInfo.pickup_store_id !== null);
    },
    closeModal() {
      this.internalShow = false;
    },
    formatMoney(val) {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);
    },
    formatDate(dateStr) {
      if (!dateStr) return 'N/A';
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
  },
  mounted() {
    const { store } = this.authUserData.user_claims;
    this.currentStoreId = store;
  },
};
</script>

<style scoped>
.background-light {
  background-color: #f8fafc;
}
.border-table {
  border: 1px solid #e2e8f0;
}
.delivery-store-card {
  min-width: 280px;
}
.delivery-store-list {
  max-height: 220px;
  overflow-y: auto;
}
</style>
